import { logger } from '@/utils/logger'

// Cross-tab message types
export type CrossTabMessageType =
  | 'ANALYSIS_COMPLETED'
  | 'ANALYSIS_FAILED'
  | 'ANALYSIS_COMPLETION_NOTIFICATION'
  | 'TOAST_NOTIFICATION'

// Cross-tab message structure
export interface CrossTabMessage {
  type: CrossTabMessageType
  timestamp: number
  tabId: string
  data: unknown
}

// Notification data structure
export interface NotificationData {
  title: string
  description?: string
  variant?: 'default' | 'success' | 'warning' | 'destructive'
  duration?: number
  concurrentExecutionId?: string
  serviceName?: string
  successCount?: number
  totalCount?: number
}

// Analysis completion notification data structure
export interface AnalysisCompletionNotificationData {
  type: 'ANALYSIS_COMPLETION'
  status: 'SUCCEEDED' | 'PARTIALLY_SUCCEEDED' | 'FAILED'
  batchId: string
  spaceId: string
  serviceId: string
  serviceName: string
  metrics: {
    totalAnalyses: number
    completedAnalyses: number
    failedAnalyses: number
    successRate: number
    executionDuration?: number
  }
  timestamp: string
  executedBy: {
    id: string
    name: string
  }
}

// Cross-tab communication service
class CrossTabCommunicationService {
  private broadcastChannel: BroadcastChannel | null = null
  private listeners: Map<CrossTabMessageType, Array<(data: unknown) => void>> = new Map()
  private tabId: string
  private isSupported: boolean

  constructor() {
    this.tabId = this.generateTabId()
    this.isSupported = this.checkBroadcastChannelSupport()

    if (this.isSupported) {
      this.initializeBroadcastChannel()
    } else {
      this.initializeLocalStorageFallback()
    }
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
  }

  private checkBroadcastChannelSupport(): boolean {
    return typeof BroadcastChannel !== 'undefined'
  }

  private initializeBroadcastChannel(): void {
    try {
      this.broadcastChannel = new BroadcastChannel('ai-hack-notifications')
      this.broadcastChannel.onmessage = event => {
        this.handleMessage(event.data)
      }
      logger.info('[CrossTabCommunication] BroadcastChannel initialized')
    } catch (error) {
      logger.error('[CrossTabCommunication] Failed to initialize BroadcastChannel:', error)
      this.isSupported = false
      this.initializeLocalStorageFallback()
    }
  }

  private initializeLocalStorageFallback(): void {
    // Listen for localStorage changes as fallback
    window.addEventListener('storage', event => {
      if (event.key === 'ai-hack-cross-tab-message' && event.newValue) {
        try {
          const message = JSON.parse(event.newValue)
          this.handleMessage(message)
        } catch (_error) {
          logger.error('[CrossTabCommunication] Failed to parse localStorage message:', _error)
        }
      }
    })
    logger.info('[CrossTabCommunication] localStorage fallback initialized')
  }

  private handleMessage(message: CrossTabMessage): void {
    // Don't handle messages from the same tab
    if (message.tabId === this.tabId) {
      return
    }

    // Check if message is recent (within 30 seconds)
    const messageAge = Date.now() - message.timestamp
    if (messageAge > 30000) {
      return
    }

    const listeners = this.listeners.get(message.type) || []
    listeners.forEach(listener => {
      try {
        listener(message.data)
      } catch (error) {
        logger.error(`[CrossTabCommunication] Error in listener for ${message.type}:`, error)
      }
    })

    logger.debug(`[CrossTabCommunication] Received message:`, message)
  }

  public broadcast(type: CrossTabMessageType, data: unknown): void {
    const message: CrossTabMessage = {
      type,
      timestamp: Date.now(),
      tabId: this.tabId,
      data,
    }

    if (this.isSupported && this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(message)
      } catch (error) {
        logger.error('[CrossTabCommunication] Failed to broadcast via BroadcastChannel:', error)
        this.broadcastViaLocalStorage(message)
      }
    } else {
      this.broadcastViaLocalStorage(message)
    }

    logger.debug(`[CrossTabCommunication] Broadcasted message:`, message)
  }

  private broadcastViaLocalStorage(message: CrossTabMessage): void {
    try {
      localStorage.setItem('ai-hack-cross-tab-message', JSON.stringify(message))

      // Clear the message after a short delay to prevent it from being processed again
      setTimeout(() => {
        try {
          localStorage.removeItem('ai-hack-cross-tab-message')
        } catch (_error) {
          // Ignore errors when clearing localStorage
        }
      }, 100)
    } catch (error) {
      logger.error('[CrossTabCommunication] Failed to broadcast via localStorage:', error)
    }
  }

  public subscribe(type: CrossTabMessageType, listener: (data: unknown) => void): () => void {
    const listeners = this.listeners.get(type) || []
    listeners.push(listener)
    this.listeners.set(type, listeners)

    // Return unsubscribe function
    return () => {
      const currentListeners = this.listeners.get(type) || []
      const index = currentListeners.indexOf(listener)
      if (index > -1) {
        currentListeners.splice(index, 1)
        this.listeners.set(type, currentListeners)
      }
    }
  }

  public broadcastAnalysisCompleted(data: {
    concurrentExecutionId: string
    serviceName: string
    successCount: number
    totalCount: number
  }): void {
    this.broadcast('ANALYSIS_COMPLETED', data)
  }

  public broadcastAnalysisFailed(data: {
    concurrentExecutionId: string
    serviceName: string
    error: string
  }): void {
    this.broadcast('ANALYSIS_FAILED', data)
  }

  public broadcastToastNotification(notificationData: NotificationData): void {
    this.broadcast('TOAST_NOTIFICATION', notificationData)
  }

  public broadcastAnalysisCompletionNotification(data: AnalysisCompletionNotificationData): void {
    this.broadcast('ANALYSIS_COMPLETION_NOTIFICATION', data)
  }

  public subscribeToAnalysisCompleted(listener: (data: unknown) => void): () => void {
    return this.subscribe('ANALYSIS_COMPLETED', listener)
  }

  public subscribeToAnalysisFailed(listener: (data: unknown) => void): () => void {
    return this.subscribe('ANALYSIS_FAILED', listener)
  }

  public subscribeToToastNotifications(listener: (data: NotificationData) => void): () => void {
    return this.subscribe('TOAST_NOTIFICATION', (data: unknown) => {
      listener(data as NotificationData)
    })
  }

  public subscribeToAnalysisCompletionNotifications(
    listener: (data: AnalysisCompletionNotificationData) => void
  ): () => void {
    return this.subscribe('ANALYSIS_COMPLETION_NOTIFICATION', (data: unknown) => {
      listener(data as AnalysisCompletionNotificationData)
    })
  }

  public destroy(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close()
      this.broadcastChannel = null
    }
    this.listeners.clear()
    logger.info('[CrossTabCommunication] Service destroyed')
  }
}

// Create and export a singleton instance
export const crossTabCommunication = new CrossTabCommunicationService()

// Export for testing purposes
export { CrossTabCommunicationService }
